/* ==========================================================================
   CasMarkt — product page behaviour (DESIGN-v2.3, builder P)
   Loaded (defer) from sections/main-product.liquid.

   Owns: accordions, gallery cross-fade helpers (dots + hover-zoom lens),
   the mobile sticky bar, the Add-to-bag success state and the sticky
   buy-box guard. Every animation is transform/opacity or a grid-track
   interpolation, and all of it is skipped under prefers-reduced-motion.

   It never touches Spotlight's own contracts: media-gallery, variant-selects,
   product-form and cart-drawer keep doing exactly what they did.
   ========================================================================== */
(function () {
  'use strict';

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  function each(root, selector, fn) {
    if (!root) return;
    Array.prototype.forEach.call(root.querySelectorAll(selector), fn);
  }

  /* ------------------------------------------------------------------ */
  /* 1. Accordions — animated on grid-template-rows, one <button> each.  */
  /* ------------------------------------------------------------------ */
  function accordions(root) {
    each(root, '[data-cm-acc]', function (acc) {
      if (acc.cmBound) return;
      acc.cmBound = true;

      var sum = acc.querySelector('[data-cm-acc-sum]');
      if (!sum) return;

      sum.addEventListener('click', function () {
        var open = acc.getAttribute('data-open') === 'true';
        acc.setAttribute('data-open', open ? 'false' : 'true');
        sum.setAttribute('aria-expanded', open ? 'false' : 'true');
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* 2. Gallery — mobile dots + desktop hover-zoom lens.                 */
  /* ------------------------------------------------------------------ */
  function gallery(root) {
    each(root, '[data-cm-gallery]', function (gal) {
      if (gal.cmBound) return;
      gal.cmBound = true;

      var list = gal.querySelector('.product__media-list');
      var dots = gal.querySelector('[data-cm-dots]');

      /* --- dots (mobile carousel) --- */
      if (list && dots) {
        var slides = Array.prototype.slice.call(list.querySelectorAll('.product__media-item'));
        dots.setAttribute('data-count', String(slides.length));

        if (slides.length > 1) {
          slides.forEach(function (slide, i) {
            var b = document.createElement('button');
            b.type = 'button';
            b.className = 'cm-gal__dot' + (i === 0 ? ' is-on' : '');
            b.setAttribute('aria-label', 'Show image ' + (i + 1) + ' of ' + slides.length);
            b.addEventListener('click', function () {
              list.scrollTo({
                left: slide.offsetLeft,
                behavior: reduce.matches ? 'auto' : 'smooth'
              });
            });
            dots.appendChild(b);
          });

          var buttons = Array.prototype.slice.call(dots.children);
          var ticking = false;
          var sync = function () {
            ticking = false;
            var mid = list.scrollLeft + list.clientWidth / 2;
            var best = 0;
            var bestD = Infinity;
            slides.forEach(function (slide, i) {
              var d = Math.abs(slide.offsetLeft + slide.offsetWidth / 2 - mid);
              if (d < bestD) {
                bestD = d;
                best = i;
              }
            });
            buttons.forEach(function (b, i) {
              b.classList.toggle('is-on', i === best);
            });
          };
          list.addEventListener(
            'scroll',
            function () {
              if (ticking) return;
              ticking = true;
              window.requestAnimationFrame(sync);
            },
            { passive: true }
          );
        }
      }

      /* --- hover-zoom lens (desktop, pointer devices only) --- */
      var fine = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
      var frame = null;

      var moveTo = function (x, y) {
        frame = null;
        gal.style.setProperty('--cm-zx', x.toFixed(2) + '%');
        gal.style.setProperty('--cm-zy', y.toFixed(2) + '%');
      };

      gal.addEventListener(
        'pointermove',
        function (e) {
          if (!fine.matches || reduce.matches) return;
          var stage = gal.querySelector('.product__media-item.is-active .media-type-image');
          if (!stage || !stage.contains(e.target)) {
            gal.removeAttribute('data-cm-zooming');
            return;
          }
          var r = stage.getBoundingClientRect();
          if (!r.width || !r.height) return;
          var x = ((e.clientX - r.left) / r.width) * 100;
          var y = ((e.clientY - r.top) / r.height) * 100;
          gal.setAttribute('data-cm-zooming', 'true');
          if (frame) return;
          frame = window.requestAnimationFrame(function () {
            moveTo(Math.max(0, Math.min(100, x)), Math.max(0, Math.min(100, y)));
          });
        },
        { passive: true }
      );

      gal.addEventListener('pointerleave', function () {
        gal.removeAttribute('data-cm-zooming');
      });

      /* Keyboard users switch media through the thumbnails; the zoom must not
         be left latched when focus moves away from the stage. */
      gal.addEventListener('focusin', function () {
        gal.removeAttribute('data-cm-zooming');
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* 3. Add-to-bag success state.                                        */
  /*    Deliberately poll-based. The button lives inside the product      */
  /*    form, and Shopify's accelerated-checkout component keeps its own  */
  /*    MutationObserver on that form: a second observer that writes back */
  /*    into the form turns into a mutation ping-pong that locks the tab. */
  /*    Reading a class costs nothing and can never feed that loop.       */
  /* ------------------------------------------------------------------ */
  function addToBag(root) {
    each(root, '[data-cm-atb]', function (btn) {
      if (btn.cmBound) return;
      btn.cmBound = true;

      var form = btn.closest('form');
      if (!form) return;

      var hideTimer = null;
      var poll = null;

      var stop = function () {
        window.clearInterval(poll);
        poll = null;
      };

      form.addEventListener('submit', function () {
        if (btn.getAttribute('aria-disabled') === 'true') return;
        btn.classList.remove('cm-is-added');
        window.clearTimeout(hideTimer);
        stop();

        var ticks = 0;
        var sawLoading = false;
        poll = window.setInterval(function () {
          ticks += 1;
          if (btn.classList.contains('loading')) {
            sawLoading = true;
            return;
          }
          /* Give the fetch a moment to even start before deciding. */
          if (!sawLoading && ticks < 6) return;
          stop();
          if (ticks >= 80) return;

          var wrapper = form
            .closest('product-form')
            .querySelector('.product-form__error-message-wrapper');
          if (wrapper && !wrapper.hasAttribute('hidden')) return;

          btn.classList.add('cm-is-added');
          hideTimer = window.setTimeout(function () {
            btn.classList.remove('cm-is-added');
          }, 1800);
        }, 120);
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* 4. Mobile sticky bar — appears once the buy box scrolls out.        */
  /* ------------------------------------------------------------------ */
  function stickyBar(root) {
    var bar = root.querySelector('[data-cm-stickybar]');
    var box = root.querySelector('[data-cm-buybox]');
    if (!bar || !box || bar.cmBound) return;
    bar.cmBound = true;

    var buyButton = root.querySelector('[data-cm-atb]');
    var priceHost = root.querySelector('[data-cm-price]');
    var mirror = bar.querySelector('[data-cm-stickybar-price]');
    var stickyBtn = bar.querySelector('[data-cm-stickybar-btn]');

    /* The sticky button carries form="…", so it belongs to the product form
       even though it sits outside it, and Shopify's accelerated-checkout
       component keeps a MutationObserver on that form. Nothing here may
       observe the form or write into it on a mutation, or the two chase each
       other and lock the tab. The mirror is therefore driven by Spotlight's
       own `variantChange` pub/sub, and every write compares first. */
    var lastPrice = null;

    var syncPrice = function () {
      if (!priceHost || !mirror) return;
      var html = priceHost.innerHTML;
      if (html === lastPrice) return;
      lastPrice = html;
      mirror.innerHTML = html;
    };

    var syncState = function () {
      if (!buyButton || !stickyBtn) return;
      var off =
        buyButton.hasAttribute('disabled') || buyButton.getAttribute('aria-disabled') === 'true';
      var source = buyButton.querySelector('.cm-atb__label') || buyButton;
      var label = off ? source.textContent.trim() || 'Sold out' : stickyBtn.dataset.label;
      if (stickyBtn.disabled !== off) stickyBtn.disabled = off;
      if (stickyBtn.textContent !== label) stickyBtn.textContent = label;
    };

    if (stickyBtn) {
      stickyBtn.dataset.label = stickyBtn.textContent.trim();
      if (buyButton) {
        stickyBtn.addEventListener('click', function () {
          /* `form=` submits the product form, which is what product-form.js
             listens for. Focus the real button first so the cart drawer hands
             focus back somewhere sensible. */
          buyButton.focus({ preventScroll: true });
        });
      }
    }

    syncPrice();
    syncState();

    /* pubsub.js declares these with `const`, so they are script-scope globals
       rather than window properties — `typeof` is the only safe probe. */
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, function () {
        window.setTimeout(function () {
          syncPrice();
          syncState();
        }, 0);
      });
    }

    if (!('IntersectionObserver' in window)) {
      bar.classList.add('is-up');
      return;
    }

    new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          bar.classList.toggle('is-up', !entry.isIntersecting && entry.boundingClientRect.top < 0);
        });
      },
      { rootMargin: '0px 0px -40% 0px', threshold: 0 }
    ).observe(box);
  }

  /* ------------------------------------------------------------------ */
  /* 5. Sticky buy box guard — a box taller than the viewport must not    */
  /*    stick, or its bottom half becomes unreachable.                   */
  /* ------------------------------------------------------------------ */
  function stickyGuard(root) {
    var box = root.querySelector('[data-cm-buybox]');
    if (!box) return;

    var measure = function () {
      var top = parseFloat(getComputedStyle(box).top) || 100;
      box.classList.toggle('cm-buybox--tall', box.offsetHeight + top + 24 > window.innerHeight);
    };

    measure();
    /* Re-measure after web fonts and lazy images settle, then only on resize.
       No ResizeObserver: `cm-buybox--tall` changes layout, which is exactly
       the feedback shape a ResizeObserver reports as a loop. */
    window.setTimeout(measure, 900);
    window.setTimeout(measure, 2500);
    window.addEventListener('load', measure, { once: true });
    window.addEventListener('resize', debounceLocal(measure, 200), { passive: true });
  }

  function debounceLocal(fn, wait) {
    var t;
    return function () {
      window.clearTimeout(t);
      t = window.setTimeout(fn, wait);
    };
  }

  /* ------------------------------------------------------------------ */
  /* 6. Reveals — cm-animations.js owns `.cm-reveal`; this is the guard   */
  /*    for the case where that file is absent or failed.                */
  /* ------------------------------------------------------------------ */
  function reveals(root) {
    if (document.documentElement.classList.contains('cm-js')) return;
    if (!('IntersectionObserver' in window) || reduce.matches) {
      each(root, '.cm-reveal', function (el) {
        el.classList.add('is-in');
      });
      return;
    }
    document.documentElement.classList.add('cm-js');
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0 }
    );
    each(root, '.cm-reveal', function (el) {
      io.observe(el);
    });
  }

  /* ------------------------------------------------------------------ */
  /* 7. Recommendations arrive by fetch, long after cm-animations.js has  */
  /*    swept the document, so their `.cm-reveal` children would stay at  */
  /*    opacity 0 forever. Reveal them as soon as they land, staggered.   */
  /* ------------------------------------------------------------------ */
  function recsReveal(root) {
    each(root, '.cm-recs product-recommendations', function (host) {
      if (host.cmBound) return;
      host.cmBound = true;

      var show = function () {
        var items = host.querySelectorAll('.cm-reveal:not(.is-in)');
        if (!items.length) return false;
        Array.prototype.forEach.call(items, function (el, i) {
          if (reduce.matches) {
            el.classList.add('is-in');
            return;
          }
          window.setTimeout(function () {
            el.classList.add('is-in');
          }, 60 * i);
        });
        return true;
      };

      /* A short poll rather than a MutationObserver: see addToBag() — no
         observer of this page's own DOM survives contact with Shopify's
         accelerated-checkout component, and this only has to catch one
         fetch that lands within a few seconds of the band scrolling in. */
      if (show()) return;
      var ticks = 0;
      var poll = window.setInterval(function () {
        ticks += 1;
        if (show() || ticks > 120) window.clearInterval(poll);
      }, 250);
    });
  }

  /* ------------------------------------------------------------------ */
  function init(root) {
    root = root || document;
    each(root, '[data-cm-pdp]', function (pdp) {
      accordions(pdp);
      gallery(pdp);
      addToBag(pdp);
      stickyBar(pdp);
      stickyGuard(pdp);
    });
    /* The details band and contact strip live outside <product-info>. */
    accordions(root);
    recsReveal(root);
    reveals(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  /* Theme editor: sections are swapped in wholesale. */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
